import * as React from 'react';
import { observer } from "mobx-react-lite";
import { Button, IconButton, Spinner, Typography } from "@material-tailwind/react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';

import { orderController } from "../Controllers/OrderController";
import { StockistBasketItemDTO } from "../../../Common/DTOs/Basket/StockistBasketItemDTO";
import { stockiestPortalController } from "../../../Common/Controllers/StockiestPortalController";
import { basketController } from '../../../Common/Controllers/BasketController';

const FREEBIE_EVERY_N = 10;

export const ReviewOrderSubpage = observer(() => {
  const navigate = useNavigate();
  const CURRENT_STEP = 0;
  const NEXT_STEP = 1;
  orderController.activeStep = CURRENT_STEP;

  const navigateToDeliveryDetails = () => {
    orderController.activeStep = NEXT_STEP
    navigate("/checkout/step-2")
  }

  return (
    <>
      <div className="animate-fadeIn flex flex-col h-full">
        <div className="flex flex-col h-full">
          <div className="w-full h-0 flex flex-col flex-auto overflow-y-auto px-0 md:px-6">
            {stockiestPortalController.purchasableOrderedProducts?.map(op => <OrderedItem orderedProduct={op}
              key={op.issue.id} />)}
          </div>

          <div className="bg-gray-800 border-y-2 overflow-hidden sm:py-2 xlh:py-4 grid grid-cols-1 items-center sm:gap-2 sm:grid-cols-3 sm:grid-rows-1 sm:text-center">
            <div className="hidden sm:block"></div>
            <div className="justify-self-center flex flex-col justify-center items-center text-white text-center sm:col-start-2">
              <Typography variant="h4" color="white" className="text-base lgh:text-xl xlh:text-3xl">
                Magazine total: £{stockiestPortalController.totalPriceInclDiscountInPounds?.toFixed(2)}
              </Typography>

              {!!stockiestPortalController.deliveryFrom && stockiestPortalController.deliveryFrom > 0 && (
                <Typography
                  variant="h6"
                  color="white"
                  className="text-light-grey text-xs lgh:text-sm font-light italic mt-1"
                >
                  With delivery from <span className="font-bold">£{basketController.basket?.deliveryFrom?.price?.toFixed(2)}</span>
                  {stockiestPortalController.deliveryAddress && (
                    <div className="ml-2 inline-flex items-center bg-main rounded p-1 text-xs text-light-grey gap-0.5">
                      <MapPinIcon className="h-3 w-3" />
                      <span>
                        {stockiestPortalController.deliveryAddress.postCode}{" "}
                        {stockiestPortalController.deliveryAddress.country}
                      </span>
                    </div>
                  )}
                </Typography>
              )}
            </div>

            <div className="flex justify-center sm:justify-end col-span-3 sm:col-span-1">
              <Button
                size="md"
                className="rounded-none h-auto w-full sm:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={navigateToDeliveryDetails}
                disabled={stockiestPortalController.purchasableOrderedProducts.length === 0
                  || stockiestPortalController.isBasketDataLoading}
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-lg xlh:text-xl">Delivery Details</span>
                  <ArrowRightIcon color="black" className="h-4 w-4" />
                </div>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </>
  )
})

type OrderedItemProps = {
  orderedProduct: StockistBasketItemDTO
}

export const OrderedItem = observer(({ orderedProduct }: OrderedItemProps) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = React.useState(orderedProduct.quantity.toString());
  const changeOrderAmount = (deltaOrNewValue: number, isDelta = true) => {
    if (isNaN(deltaOrNewValue) || (!isDelta && deltaOrNewValue < 0))
      return;

    let newQuantity = isDelta
      ? orderedProduct.quantity + deltaOrNewValue
      : deltaOrNewValue;

    newQuantity = Math.max(1, newQuantity);
    stockiestPortalController.updateOrderedProduct(orderedProduct, newQuantity);
  };

  useEffect(() => {
    setInputValue(orderedProduct.quantity.toString());
  }, [orderedProduct, orderedProduct.quantity]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (val === "") {
      setInputValue("");
      return;
    }

    const num = parseInt(val);
    if (isNaN(num)) return;

    setInputValue(Math.max(1, Math.min(num, orderedProduct.maxAvailableQuantity)).toString());
  };

  const handleBlur = () => {
    if (inputValue === "") {
      // Note: revert to original value if left empty
      setInputValue(orderedProduct.quantity.toString());
      return;
    }

    const num = parseInt(inputValue);
    if (!isNaN(num) && num !== orderedProduct.quantity) {
      changeOrderAmount(num, false);
    }
  };

  return (
    <>
      <div className="flex flex-col xsm:flex-row border-b py-2 xlh:py-4 gap-2 last:border-b-0">
        <div className="basis-3/4 flex flex-row gap-2 sm:gap-4">
          <img
            src={orderedProduct.issue.imageURL_230x320}
            className="w-[120px] xlh:w-[160px] h-[172px] xlh:h-[230px] hover:shadow-[0_0_12px_#fff] hover:shadow-white transition ease-in-out cursor-pointer"
            alt={`${orderedProduct.issue.identity} cover image`}
            title={`See this issue of ${orderedProduct.issue.magazine.title}`}
            onClick={() => navigate(`/issue/${orderedProduct.issue.id}`)}
          />
          <div className="flex flex-col">
            <Typography variant="h5" color="white" className="text-base smh:text-lg mdh:text-xl lgh:text-2xl xlh:text-3xl">
              <Link className="hover:text-gray-400 transition cursor-pointer" to={orderedProduct.issue.magazine.stockistMagazineUrl}>
                {orderedProduct.issue.magazine.title}
              </Link>
              {" "}-{" "}
              <Link className="hover:text-gray-400 transition cursor-pointer" to={`/issue/${orderedProduct.issue.id}`}>
                {orderedProduct.issue.identity}
              </Link>
              <IconButton
                size="sm"
                title="Remove from basket"
                className="ml-1 sm:ml-2 mb-1 h-6 w-6 smh:h-8 smh:w-8 text-red-400 hover:text-red-600"
                disabled={orderedProduct.isUpdatingQuantity}
                onClick={() => stockiestPortalController.removeOrderedProduct(orderedProduct)}
              >
                <TrashIcon className="h-4 w-4 smh:h-6 smh:w-6" />
              </IconButton>
            </Typography>

            <Typography variant="h6" color="white" className="flex flex-row gap-2 text-sm mdh:text-base lgh:text-xl">
              <div className="flex flex-row items-center">
                <Button
                  variant="outlined"
                  title="Decrease quantity"
                  color="red"
                  size="sm"
                  className="h-10 !border-light-grey/50 !text-white text-md hover:!bg-red-500 hover:!border-red-500 transition rounded-r-none"
                  onClick={() => changeOrderAmount(-1, true)}
                  disabled={orderedProduct.isUpdatingQuantity || orderedProduct.quantity <= 0}
                >
                  -
                </Button>
                <input
                  type="number"
                  name="quantity"
                  min={1}
                  max={orderedProduct.maxAvailableQuantity}
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="w-12 h-10 text-center bg-gray-700 text-white border border-gray-600 focus:outline-none !appearance-none rounded-none"
                  disabled={orderedProduct.isUpdatingQuantity}
                />
                <Button
                  variant="outlined"
                  title="Increase quantity"
                  color="green"
                  size="sm"
                  className="h-10 !border-light-grey/50 !text-white text-md hover:!bg-green-500 hover:!border-green-500 transition rounded-l-none"
                  onClick={() => changeOrderAmount(1, true)}
                  disabled={orderedProduct.isUpdatingQuantity || orderedProduct.quantity >= orderedProduct.maxAvailableQuantity}
                >
                  +
                </Button>
                {orderedProduct.isUpdatingQuantity && <Spinner />}
              </div>
            </Typography>

            {orderedProduct.isFreebieEligible && (() => {
              const eligibleQty = orderedProduct.historicalSummary?.freebieEligibleQuantity ?? 0;
              const alreadyEarned = orderedProduct.historicalSummary?.freebiesEarned ?? 0;
              const newEligibleTotal = eligibleQty + orderedProduct.quantity;
              const localFreebiesDue = Math.max(0, Math.floor(newEligibleTotal / FREEBIE_EVERY_N) - alreadyEarned);
              const progressInCycle = newEligibleTotal % FREEBIE_EVERY_N;
              return (
                <span className="text-xs text-amber-300 mt-1">
                  {localFreebiesDue > 0
                    ? `🎁 +${localFreebiesDue} free (${progressInCycle}/${FREEBIE_EVERY_N} to next)`
                    : `🎁 ${progressInCycle}/${FREEBIE_EVERY_N} to next free copy`}
                </span>
              );
            })()}

            <Typography variant="h6" color="white" className="text-sm mdh:text-base lgh:text-xl">
              Cover Price: {orderedProduct.issue.priceDisplay}
            </Typography>
            {orderedProduct.issueDiscountPercentage > 0 &&
              <>
                <Typography variant="h6" color="green" className="text-sm mdh:text-base lgh:text-xl">
                  Discount: {orderedProduct.issueDiscountPercentage}%
                </Typography>
                <Typography variant="h6" color="white" className="text-sm mdh:text-base lgh:text-xl">
                  Your price: £{orderedProduct.discountedIssuePrice.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </>
            }
          </div>
        </div>

        <div className="basis-1/4 flex flex-col gap-1 mt-2">
          <div className="flex flex-row gap-1">
            <Typography variant="h6" color="white" className="basis-3/5 text-sm mdh:text-base lgh:text-xl">Price</Typography>
            <Typography variant="h6" color="white" className="basis-2/5 text-sm mdh:text-base lgh:text-xl">
              £{orderedProduct.totalIssuePrice.toFixed(2)}
            </Typography>
          </div>
          {(orderedProduct.totalIssuePrice - orderedProduct.totalDiscountedIssuePrice) > 0 &&
            <div className="flex flex-row gap-1">
              <Typography variant="h6" color="green" className="basis-3/5 text-sm mdh:text-base lgh:text-xl">Discounts</Typography>
              <Typography variant="h6" color="green" className="basis-2/5 text-sm mdh:text-base lgh:text-xl whitespace-nowrap">
                - £{(orderedProduct.totalIssuePrice - orderedProduct.totalDiscountedIssuePrice).toFixed(2)}
              </Typography>
            </div>
          }
          <div className="flex flex-row gap-1">
            <Typography variant="h6" color="white" className="basis-3/5 text-sm mdh:text-base lgh:text-xl">Weight:</Typography>
            <Typography variant="h6" color="white" className="basis-2/5 text-sm mdh:text-base lgh:text-xl">
              {orderedProduct.totalIssueWeightKilograms}kg
            </Typography>
          </div>
          <div className="border-t my-2" />
          <div className="flex flex-row gap-1">
            <Typography variant="h6" color="white" className="basis-3/5 text-sm mdh:text-base lgh:text-xl">Total:</Typography>
            <Typography variant="h6" color="white" className="basis-2/5 text-sm mdh:text-base lgh:text-xl">
              £{orderedProduct.totalDiscountedIssuePrice.toFixed(2)}
            </Typography>
          </div>
        </div>
      </div>
    </>
  )
})





